import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { ArrowLeft, User, Mail, Phone, MapPin, FileText, Users, DollarSign, Edit2, Plus, Save, Key, Upload, File, Trash2, Download, Eye, CreditCard, Calendar, CheckCircle2, XCircle, RefreshCw } from "lucide-react";
import { NestIcon } from "../../components/NestIcon";
import { AgentLayout } from "./AgentDashboard";
import { API } from "../../App";
import axios from "axios";
import { toast } from "sonner";

export default function AgentCustomerDetails() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const fileInputRef = useRef(null);
  
  // Edit customer state
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zip_code: ""
  });
  const [saving, setSaving] = useState(false);

  // Set password state  
  const [isSetPasswordOpen, setIsSetPasswordOpen] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [sendPasswordEmail, setSendPasswordEmail] = useState(true);
  const [settingPassword, setSettingPassword] = useState(false);

  // Create quote state
  const [isQuoteDialogOpen, setIsQuoteDialogOpen] = useState(false);
  const [quoteForm, setQuoteForm] = useState({
    monthly_rent: "",
    lease_start_date: "",
    lease_end_date: "",
    employer_name: "",
    employment_start_date: "",
    annual_income: ""
  });
  const [creatingQuote, setCreatingQuote] = useState(false);
  
  // Password reset state
  const [isResetPasswordOpen, setIsResetPasswordOpen] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [sendEmailOnReset, setSendEmailOnReset] = useState(true);
  
  // Document upload state
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    document_type: "id_verification",
    notes: ""
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [documents, setDocuments] = useState([]);
  
  // Document preview state
  const [previewDocument, setPreviewDocument] = useState(null);
  
  // Customer payments state
  const [customerPayments, setCustomerPayments] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [exportingPayments, setExportingPayments] = useState(false);
  
  // Policy detail dialog state
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [policyPayments, setPolicyPayments] = useState([]);
  const [loadingPolicyDetails, setLoadingPolicyDetails] = useState(false);

  useEffect(() => {
    fetchCustomerDetails();
    fetchCustomerDocuments();
    fetchCustomerPayments();
  }, [userId]);

  const fetchCustomerDetails = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.get(`${API}/agent/customers/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
      // Initialize edit form with user data
      const user = response.data.user;
      setEditForm({
        full_name: user.full_name || "",
        email: user.email || "",
        phone: user.phone || "",
        address: user.address || "",
        city: user.city || "",
        state: user.state || "",
        zip_code: user.zip_code || ""
      });
    } catch (error) {
      toast.error("Failed to load customer details");
      navigate("/agent/customers");
    } finally {
      setLoading(false);
    }
  };
  
  const fetchCustomerDocuments = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.get(`${API}/agent/customers/${userId}/documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data);
    } catch (error) {
      console.log("Failed to load documents");
    }
  };

  const fetchCustomerPayments = async () => {
    setLoadingPayments(true);
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.get(`${API}/agent/customers/${userId}/payments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomerPayments(response.data);
    } catch (error) {
      console.log("Failed to load payments");
    } finally {
      setLoadingPayments(false);
    }
  };

  const fetchPolicyDetails = async (policyId) => {
    setLoadingPolicyDetails(true);
    const token = localStorage.getItem("agent_token");
    try {
      const [policyRes, paymentsRes] = await Promise.all([
        axios.get(`${API}/agent/policies/${policyId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/agent/policies/${policyId}/payments`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setSelectedPolicy(policyRes.data);
      setPolicyPayments(paymentsRes.data);
    } catch (error) {
      toast.error("Failed to load policy details");
    } finally {
      setLoadingPolicyDetails(false);
    }
  };

  const handleExportCustomerPayments = async () => {
    setExportingPayments(true);
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.get(`${API}/agent/customers/${userId}/payments/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Customer_Payments_${userId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Payment history exported successfully!");
    } catch (error) {
      toast.error("Failed to export payments");
    } finally {
      setExportingPayments(false);
    }
  };

  const handleSaveCustomer = async () => {
    setSaving(true);
    const token = localStorage.getItem("agent_token");
    try {
      await axios.put(
        `${API}/agent/customers/${userId}`,
        editForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Customer information updated");
      setIsEditing(false);
      fetchCustomerDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update customer");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateQuote = async () => {
    // Validate form
    if (!quoteForm.monthly_rent || !quoteForm.lease_start_date || !quoteForm.lease_end_date ||
        !quoteForm.employer_name || !quoteForm.employment_start_date || !quoteForm.annual_income) {
      toast.error("Please fill in all fields");
      return;
    }

    setCreatingQuote(true);
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.post(
        `${API}/agent/customers/${userId}/quotes`,
        {
          user_id: userId,
          monthly_rent: parseFloat(quoteForm.monthly_rent),
          lease_start_date: quoteForm.lease_start_date,
          lease_end_date: quoteForm.lease_end_date,
          employer_name: quoteForm.employer_name,
          employment_start_date: quoteForm.employment_start_date,
          annual_income: parseFloat(quoteForm.annual_income)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Quote created: ${response.data.quote_id}`);
      setIsQuoteDialogOpen(false);
      setQuoteForm({
        monthly_rent: "",
        lease_start_date: "",
        lease_end_date: "",
        employer_name: "",
        employment_start_date: "",
        annual_income: ""
      });
      fetchCustomerDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create quote");
    } finally {
      setCreatingQuote(false);
    }
  };
  
  const handleResetPassword = async () => {
    setResettingPassword(true);
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.post(
        `${API}/agent/customers/${userId}/reset-password`,
        { send_email: sendEmailOnReset },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (sendEmailOnReset) {
        toast.success(`Password reset! Email sent to ${response.data.customer_email}`);
      } else {
        toast.success(`Temporary password: ${response.data.temporary_password}`, { duration: 15000 });
      }
      setIsResetPasswordOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setResettingPassword(false);
    }
  };

  const handleSetPassword = async () => {
    if (!newPassword || newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setSettingPassword(true);
    const token = localStorage.getItem("agent_token");
    try {
      await axios.post(
        `${API}/agent/customers/${userId}/set-password`,
        { new_password: newPassword, send_email: sendPasswordEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Password set successfully");
      setIsSetPasswordOpen(false);
      setNewPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to set password");
    } finally {
      setSettingPassword(false);
    }
  };
  
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        toast.error("File size must be less than 10MB");
        return;
      }
      setSelectedFile(file);
    }
  };
  
  const handleUploadDocument = async () => {
    if (!selectedFile) {
      toast.error("Please select a file");
      return;
    }
    
    setUploading(true);
    const token = localStorage.getItem("agent_token");
    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("document_type", uploadForm.document_type);
    if (uploadForm.notes) formData.append("notes", uploadForm.notes);
    
    try {
      await axios.post(
        `${API}/agent/customers/${userId}/documents`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          } 
        }
      );
      toast.success("Document uploaded successfully");
      setIsUploadDialogOpen(false);
      setSelectedFile(null);
      setUploadForm({ document_type: "id_verification", notes: "" });
      fetchCustomerDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload document");
    } finally {
      setUploading(false);
    }
  };
  
  const handleDeleteDocument = async (documentId) => {
    if (!confirm("Are you sure you want to delete this document?")) return;
    
    const token = localStorage.getItem("agent_token");
    try {
      await axios.delete(`${API}/agent/customers/${userId}/documents/${documentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Document deleted");
      fetchCustomerDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete document");
    }
  };
  
  const handlePreviewDocument = async (documentId) => {
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.get(`${API}/agent/customers/${userId}/documents/${documentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreviewDocument(response.data);
    } catch (error) {
      toast.error("Failed to load document preview");
    }
  };
  
  const getDocumentTypeLabel = (type) => {
    const labels = {
      id_verification: "ID Verification",
      employment_proof: "Employment Proof",
      lease_agreement: "Lease Agreement",
      income_proof: "Income Proof",
      termination_letter: "Termination Letter",
      unemployment_benefits: "Unemployment Benefits",
      general: "General Document"
    };
    return labels[type] || type;
  };
  
  const handleDownloadQuoteContract = async (quoteId) => {
    const token = localStorage.getItem("agent_token");
    try {
      toast.loading("Generating contract...", { id: "contract" });
      const response = await axios.get(`${API}/agent/quotes/${quoteId}/contract/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { 
        type: response.headers['content-type'] || 'application/pdf' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `TheRentPayer_Quote_${quoteId}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success("Contract downloaded!", { id: "contract" });
    } catch (error) {
      toast.error("Failed to download contract", { id: "contract" });
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-100 text-yellow-800",
      approved: "bg-green-100 text-green-800",
      rejected: "bg-red-100 text-red-800",
      active: "bg-blue-100 text-blue-800",
      under_review: "bg-purple-100 text-purple-800",
      paid: "bg-emerald-100 text-emerald-800",
      converted: "bg-indigo-100 text-indigo-800"
    };
    return styles[status] || "bg-gray-100 text-gray-800";
  };

  if (loading) {
    return (
      <AgentLayout title="Customer Details">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </AgentLayout>
    );
  }

  if (!data) return null;

  const { user, policies, claims, quotes, beneficiaries } = data;

  return (
    <AgentLayout title="Customer Details">
      <Button 
        variant="ghost" 
        className="mb-6"
        onClick={() => navigate("/agent/customers")}
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Customers
      </Button>

      {/* Customer Header */}
      <div className="flex items-start gap-6 mb-8">
        <div className="bg-primary/10 p-4 rounded-full">
          <User className="h-12 w-12 text-primary" />
        </div>
        <div className="flex-1">
          {!isEditing ? (
            <>
              <div className="flex items-center gap-3">
                <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Young Serif, serif' }}>
                  {user.full_name || "N/A"}
                </h1>
                <Button variant="outline" size="sm" onClick={() => setIsEditing(true)} data-testid="edit-customer-btn">
                  <Edit2 className="h-4 w-4 mr-1" />
                  Edit
                </Button>
              </div>
              <p className="text-muted-foreground font-mono text-sm">{user.user_id}</p>
              <div className="flex flex-wrap gap-4 mt-3 text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Mail className="h-4 w-4" />
                  {user.email}
                </div>
                {user.phone && (
                  <div className="flex items-center gap-1">
                    <Phone className="h-4 w-4" />
                    {user.phone}
                  </div>
                )}
                {user.city && user.state && (
                  <div className="flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    {user.city}, {user.state}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="space-y-4">
              <h2 className="text-xl font-bold">Edit Customer Information</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Full Name</Label>
                  <Input
                    value={editForm.full_name}
                    onChange={(e) => setEditForm({...editForm, full_name: e.target.value})}
                    data-testid="edit-customer-name"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                    data-testid="edit-customer-email"
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={editForm.phone}
                    onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                    data-testid="edit-customer-phone"
                  />
                </div>
                <div>
                  <Label>Address</Label>
                  <Input
                    value={editForm.address}
                    onChange={(e) => setEditForm({...editForm, address: e.target.value})}
                  />
                </div>
                <div>
                  <Label>City</Label>
                  <Input
                    value={editForm.city}
                    onChange={(e) => setEditForm({...editForm, city: e.target.value})}
                  />
                </div>
                <div>
                  <Label>State</Label>
                  <Input
                    value={editForm.state}
                    onChange={(e) => setEditForm({...editForm, state: e.target.value})}
                  />
                </div>
                <div>
                  <Label>ZIP Code</Label>
                  <Input
                    value={editForm.zip_code}
                    onChange={(e) => setEditForm({...editForm, zip_code: e.target.value})}
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleSaveCustomer} disabled={saving} data-testid="save-customer-btn">
                  <Save className="h-4 w-4 mr-1" />
                  {saving ? "Saving..." : "Save Changes"}
                </Button>
                <Button variant="outline" onClick={() => setIsEditing(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-4">
          <div className="flex gap-4 text-center">
            <div className="bg-muted px-4 py-2 rounded-lg">
              <p className="text-2xl font-bold text-primary">{policies.length}</p>
              <p className="text-xs text-muted-foreground">Policies</p>
            </div>
            <div className="bg-muted px-4 py-2 rounded-lg">
              <p className="text-2xl font-bold text-amber-600">{claims.length}</p>
              <p className="text-xs text-muted-foreground">Claims</p>
            </div>
          </div>
          {/* Create Quote Button */}
          <Dialog open={isQuoteDialogOpen} onOpenChange={setIsQuoteDialogOpen}>
            <DialogTrigger asChild>
              <Button className="w-full" data-testid="create-quote-btn">
                <Plus className="h-4 w-4 mr-1" />
                Create Quote
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Young Serif, serif' }}>
                  Create Quote for {user.full_name || user.email}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div>
                  <Label>Monthly Rent ($)</Label>
                  <Input
                    type="number"
                    placeholder="e.g., 1500"
                    value={quoteForm.monthly_rent}
                    onChange={(e) => setQuoteForm({...quoteForm, monthly_rent: e.target.value})}
                    data-testid="quote-monthly-rent"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Lease Start Date</Label>
                    <Input
                      type="date"
                      value={quoteForm.lease_start_date}
                      onChange={(e) => setQuoteForm({...quoteForm, lease_start_date: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Lease End Date</Label>
                    <Input
                      type="date"
                      value={quoteForm.lease_end_date}
                      onChange={(e) => setQuoteForm({...quoteForm, lease_end_date: e.target.value})}
                    />
                  </div>
                </div>
                <div>
                  <Label>Employer Name</Label>
                  <Input
                    placeholder="e.g., Acme Corp"
                    value={quoteForm.employer_name}
                    onChange={(e) => setQuoteForm({...quoteForm, employer_name: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Employment Start Date</Label>
                  <Input
                    type="date"
                    value={quoteForm.employment_start_date}
                    onChange={(e) => setQuoteForm({...quoteForm, employment_start_date: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Annual Income ($)</Label>
                  <Input
                    type="number"
                    placeholder="e.g., 60000"
                    value={quoteForm.annual_income}
                    onChange={(e) => setQuoteForm({...quoteForm, annual_income: e.target.value})}
                    data-testid="quote-annual-income"
                  />
                </div>
                <Button 
                  onClick={handleCreateQuote} 
                  disabled={creatingQuote}
                  className="w-full"
                  data-testid="submit-quote-btn"
                >
                  {creatingQuote ? "Creating..." : "Create Quote"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          
          {/* Reset Password Button */}
          <Dialog open={isResetPasswordOpen} onOpenChange={setIsResetPasswordOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="w-full" data-testid="reset-password-btn">
                <Key className="h-4 w-4 mr-1" />
                Reset Password
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-sm">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Young Serif, serif' }}>
                  Reset Customer Password
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <p className="text-muted-foreground text-sm">
                  This will generate a temporary password for <strong>{user.full_name || user.email}</strong>.
                </p>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="send-email"
                    checked={sendEmailOnReset}
                    onChange={(e) => setSendEmailOnReset(e.target.checked)}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="send-email" className="cursor-pointer">
                    Send password via email
                  </Label>
                </div>
                {!sendEmailOnReset && (
                  <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                    Warning: The temporary password will be shown once. Make sure to copy it.
                  </p>
                )}
                <Button 
                  onClick={handleResetPassword}
                  disabled={resettingPassword}
                  className="w-full"
                  data-testid="confirm-reset-password"
                >
                  {resettingPassword ? "Resetting..." : "Reset Password"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Set Specific Password Button */}
          <Dialog open={isSetPasswordOpen} onOpenChange={setIsSetPasswordOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="w-full" data-testid="set-password-btn">
                <Key className="h-4 w-4 mr-1" />
                Set Password
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-sm">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Young Serif, serif' }}>
                  Set Customer Password
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <p className="text-muted-foreground text-sm">
                  Set a specific password for <strong>{user.full_name || user.email}</strong>.
                </p>
                <div>
                  <Label>New Password</Label>
                  <Input
                    type="password"
                    placeholder="Minimum 8 characters"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    data-testid="new-password-input"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="send-password-email"
                    checked={sendPasswordEmail}
                    onChange={(e) => setSendPasswordEmail(e.target.checked)}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="send-password-email" className="cursor-pointer">
                    Send password via email
                  </Label>
                </div>
                <Button 
                  onClick={handleSetPassword}
                  disabled={settingPassword || newPassword.length < 8}
                  className="w-full"
                  data-testid="confirm-set-password"
                >
                  {settingPassword ? "Setting..." : "Set Password"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          
          {/* Upload Document Button */}
          <Dialog open={isUploadDialogOpen} onOpenChange={setIsUploadDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="w-full" data-testid="upload-document-btn">
                <Upload className="h-4 w-4 mr-1" />
                Upload Document
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Young Serif, serif' }}>
                  Upload Document for Customer
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div>
                  <Label>Document Type</Label>
                  <Select value={uploadForm.document_type} onValueChange={(val) => setUploadForm({...uploadForm, document_type: val})}>
                    <SelectTrigger className="mt-1" data-testid="doc-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="photo_id">Photo ID (Required for Policy)</SelectItem>
                      <SelectItem value="employment_verification">Employment Verification Letter (Required)</SelectItem>
                      <SelectItem value="id_verification">ID Verification</SelectItem>
                      <SelectItem value="employment_proof">Employment Proof</SelectItem>
                      <SelectItem value="lease_agreement">Lease Agreement</SelectItem>
                      <SelectItem value="income_proof">Income Proof</SelectItem>
                      <SelectItem value="termination_letter">Termination Letter</SelectItem>
                      <SelectItem value="unemployment_benefits">Unemployment Benefits</SelectItem>
                      <SelectItem value="general">General Document</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Photo ID and Employment Verification are required for policy approval
                  </p>
                </div>
                <div>
                  <Label>File</Label>
                  <div 
                    className="mt-1 border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      className="hidden"
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
                      data-testid="file-input"
                    />
                    {selectedFile ? (
                      <div className="flex items-center justify-center gap-2">
                        <File className="h-5 w-5 text-primary" />
                        <span className="text-sm font-medium">{selectedFile.name}</span>
                        <span className="text-xs text-muted-foreground">
                          ({(selectedFile.size / 1024).toFixed(1)} KB)
                        </span>
                      </div>
                    ) : (
                      <div>
                        <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                        <p className="text-sm text-muted-foreground">Click to select a file</p>
                        <p className="text-xs text-muted-foreground mt-1">PDF, DOC, DOCX, JPG, PNG (max 10MB)</p>
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <Label>Notes (Optional)</Label>
                  <Input
                    placeholder="Add notes about this document..."
                    value={uploadForm.notes}
                    onChange={(e) => setUploadForm({...uploadForm, notes: e.target.value})}
                    data-testid="doc-notes"
                  />
                </div>
                <Button 
                  onClick={handleUploadDocument}
                  disabled={uploading || !selectedFile}
                  className="w-full"
                  data-testid="submit-upload"
                >
                  {uploading ? "Uploading..." : "Upload Document"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="policies">
        <TabsList className="mb-6">
          <TabsTrigger value="policies">
            <NestIcon className="h-4 w-4 mr-2" />
            Policies ({policies.length})
          </TabsTrigger>
          <TabsTrigger value="payments" data-testid="payments-tab">
            <CreditCard className="h-4 w-4 mr-2" />
            Payments ({customerPayments.length})
          </TabsTrigger>
          <TabsTrigger value="claims">
            <FileText className="h-4 w-4 mr-2" />
            Claims ({claims.length})
          </TabsTrigger>
          <TabsTrigger value="quotes">
            <DollarSign className="h-4 w-4 mr-2" />
            Quotes ({quotes.length})
          </TabsTrigger>
          <TabsTrigger value="beneficiaries">
            <Users className="h-4 w-4 mr-2" />
            Beneficiaries ({beneficiaries.length})
          </TabsTrigger>
          <TabsTrigger value="documents">
            <File className="h-4 w-4 mr-2" />
            Documents ({documents.length})
          </TabsTrigger>
        </TabsList>

        {/* Policies Tab */}
        <TabsContent value="policies">
          {policies.length > 0 ? (
            <div className="grid md:grid-cols-2 gap-4">
              {policies.map((policy) => (
                <Card 
                  key={policy.policy_id} 
                  className="card-organic cursor-pointer hover:border-primary transition-colors"
                  onClick={() => fetchPolicyDetails(policy.policy_id)}
                  data-testid={`policy-card-${policy.policy_id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <p className="font-mono text-sm text-muted-foreground">{policy.policy_id}</p>
                        <p className="text-lg font-semibold">${policy.monthly_rent}/mo rent</p>
                      </div>
                      <Badge className={getStatusBadge(policy.status)}>{policy.status}</Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Premium</p>
                        <p className="font-medium">${Number(policy.monthly_premium).toFixed(2)}/mo</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Coverage Ends</p>
                        <p className="font-medium">{new Date(policy.coverage_end_date).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t flex items-center justify-between text-xs text-muted-foreground">
                      <span>Click to view details & payment history</span>
                      <Eye className="h-4 w-4" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="card-organic">
              <CardContent className="p-8 text-center text-muted-foreground">
                No policies found
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Payments Tab */}
        <TabsContent value="payments">
          <Card className="card-organic">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle style={{ fontFamily: 'Young Serif, serif' }}>
                <CreditCard className="inline h-5 w-5 mr-2 text-primary" />
                Payment History
              </CardTitle>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={fetchCustomerPayments}
                  disabled={loadingPayments}
                  size="sm"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${loadingPayments ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                <Button 
                  onClick={handleExportCustomerPayments}
                  disabled={exportingPayments || customerPayments.length === 0}
                  size="sm"
                  data-testid="export-payments-btn"
                >
                  <Download className="h-4 w-4 mr-2" />
                  {exportingPayments ? "Exporting..." : "Export PDF"}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Payment Summary */}
              {customerPayments.length > 0 && (
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg text-center">
                    <p className="text-2xl font-bold text-green-600">
                      ${customerPayments.reduce((sum, p) => sum + (p.amount || 0), 0).toFixed(2)}
                    </p>
                    <p className="text-sm text-muted-foreground">Total Paid</p>
                  </div>
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg text-center">
                    <p className="text-2xl font-bold text-blue-600">
                      {customerPayments.reduce((sum, p) => sum + (p.months_covered || 1), 0)}
                    </p>
                    <p className="text-sm text-muted-foreground">Months Covered</p>
                  </div>
                  <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg text-center">
                    <p className="text-2xl font-bold text-purple-600">
                      {customerPayments.length}
                    </p>
                    <p className="text-sm text-muted-foreground">Transactions</p>
                  </div>
                </div>
              )}

              {/* Payments Table */}
              {customerPayments.length > 0 ? (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted">
                      <tr>
                        <th className="text-left p-3 font-medium">Date</th>
                        <th className="text-left p-3 font-medium">Policy</th>
                        <th className="text-left p-3 font-medium">Type</th>
                        <th className="text-left p-3 font-medium">Amount</th>
                        <th className="text-left p-3 font-medium">Months</th>
                        <th className="text-left p-3 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {customerPayments.map((payment, idx) => (
                        <tr key={payment.payment_id || idx} className="border-t hover:bg-muted/50">
                          <td className="p-3">
                            {payment.payment_date 
                              ? new Date(payment.payment_date).toLocaleDateString() 
                              : payment.created_at 
                                ? new Date(payment.created_at).toLocaleDateString()
                                : 'N/A'}
                          </td>
                          <td className="p-3 font-mono text-xs">
                            {payment.policy_id ? `...${payment.policy_id.slice(-8)}` : 'N/A'}
                          </td>
                          <td className="p-3">
                            <Badge variant="outline" className="text-xs">
                              {payment.payment_type || 'premium'}
                            </Badge>
                          </td>
                          <td className="p-3 font-semibold text-green-600">
                            ${(payment.amount || 0).toFixed(2)}
                          </td>
                          <td className="p-3">{payment.months_covered || 1}</td>
                          <td className="p-3">
                            <Badge className={payment.status === 'completed' || payment.status === 'succeeded' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                              {payment.status || 'completed'}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <CreditCard className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No payment history found</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Claims Tab */}
        <TabsContent value="claims">
          {claims.length > 0 ? (
            <div className="space-y-4">
              {claims.map((claim) => (
                <Card 
                  key={claim.claim_id} 
                  className="card-organic cursor-pointer hover:border-primary"
                  onClick={() => navigate(`/agent/claims/${claim.claim_id}`)}
                >
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-mono text-sm text-muted-foreground">{claim.claim_id}</p>
                        <p className="font-semibold">Job loss: {new Date(claim.job_loss_date).toLocaleDateString()}</p>
                        <p className="text-sm text-muted-foreground">{claim.last_employer}</p>
                      </div>
                      <div className="text-right">
                        <Badge className={getStatusBadge(claim.status)}>{claim.status.replace('_', ' ')}</Badge>
                        <p className="text-xs text-muted-foreground mt-2">
                          Filed: {new Date(claim.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="card-organic">
              <CardContent className="p-8 text-center text-muted-foreground">
                No claims filed
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Quotes Tab */}
        <TabsContent value="quotes">
          {quotes.length > 0 ? (
            <div className="grid md:grid-cols-2 gap-4">
              {quotes.map((quote) => (
                <Card key={quote.quote_id} className="card-organic">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <p className="font-mono text-sm text-muted-foreground">{quote.quote_id}</p>
                        <p className="text-lg font-semibold">${quote.monthly_rent}/mo rent</p>
                      </div>
                      <Badge className={getStatusBadge(quote.status)}>{quote.status}</Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Premium</p>
                        <p className="font-medium">${Number(quote.monthly_premium).toFixed(2)}/mo</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Expires</p>
                        <p className="font-medium">{new Date(quote.expires_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t">
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="w-full"
                        onClick={() => handleDownloadQuoteContract(quote.quote_id)}
                        data-testid={`download-contract-${quote.quote_id}`}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Download Contract PDF
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="card-organic">
              <CardContent className="p-8 text-center text-muted-foreground">
                No quotes found
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Beneficiaries Tab */}
        <TabsContent value="beneficiaries">
          {beneficiaries.length > 0 ? (
            <div className="grid md:grid-cols-2 gap-4">
              {beneficiaries.map((ben) => (
                <Card key={ben.beneficiary_id} className="card-organic">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="bg-primary/10 p-3 rounded-full">
                        <User className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-semibold">{ben.name}</p>
                        <p className="text-sm text-muted-foreground">{ben.relationship}</p>
                        <p className="text-sm text-muted-foreground">{ben.email}</p>
                        {ben.is_primary && (
                          <Badge className="bg-amber-100 text-amber-800 mt-1">Primary</Badge>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="card-organic">
              <CardContent className="p-8 text-center text-muted-foreground">
                No beneficiaries added
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents">
          {documents.length > 0 ? (
            <div className="space-y-4">
              {documents.map((doc) => (
                <Card key={doc.document_id} className="card-organic">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="bg-primary/10 p-3 rounded-lg">
                          <File className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium">{doc.filename}</p>
                          <div className="flex gap-2 items-center mt-1">
                            <Badge variant="outline" className="text-xs">
                              {getDocumentTypeLabel(doc.document_type)}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {(doc.size / 1024).toFixed(1)} KB
                            </span>
                            <span className="text-xs text-muted-foreground">
                              • {new Date(doc.uploaded_at).toLocaleDateString()}
                            </span>
                          </div>
                          {doc.notes && (
                            <p className="text-sm text-muted-foreground mt-1">{doc.notes}</p>
                          )}
                          {doc.uploaded_by_agent && (
                            <p className="text-xs text-muted-foreground mt-1">
                              Uploaded by agent
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handlePreviewDocument(doc.document_id)}
                          data-testid={`preview-doc-${doc.document_id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDeleteDocument(doc.document_id)}
                          data-testid={`delete-doc-${doc.document_id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="card-organic">
              <CardContent className="p-8 text-center text-muted-foreground">
                <File className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                <p>No documents uploaded</p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => setIsUploadDialogOpen(true)}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Upload First Document
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Document Preview Dialog */}
      <Dialog open={!!previewDocument} onOpenChange={(open) => !open && setPreviewDocument(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Young Serif, serif' }}>
              {previewDocument?.filename}
            </DialogTitle>
          </DialogHeader>
          {previewDocument && (
            <div className="pt-4">
              {previewDocument.content_type?.startsWith("image/") ? (
                <img 
                  src={`data:${previewDocument.content_type};base64,${previewDocument.data}`}
                  alt={previewDocument.filename}
                  className="max-w-full rounded-lg"
                />
              ) : previewDocument.content_type === "application/pdf" ? (
                <iframe
                  src={`data:application/pdf;base64,${previewDocument.data}`}
                  className="w-full h-[60vh] rounded-lg"
                  title={previewDocument.filename}
                />
              ) : (
                <div className="text-center py-8">
                  <File className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Preview not available for this file type</p>
                  <Button
                    className="mt-4"
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = `data:${previewDocument.content_type};base64,${previewDocument.data}`;
                      link.download = previewDocument.filename;
                      link.click();
                    }}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download File
                  </Button>
                </div>
              )}
              <div className="mt-4 pt-4 border-t">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Document Type</p>
                    <p className="font-medium">{getDocumentTypeLabel(previewDocument.document_type)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Size</p>
                    <p className="font-medium">{(previewDocument.size / 1024).toFixed(1)} KB</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Uploaded</p>
                    <p className="font-medium">{new Date(previewDocument.uploaded_at).toLocaleString()}</p>
                  </div>
                  {previewDocument.notes && (
                    <div>
                      <p className="text-muted-foreground">Notes</p>
                      <p className="font-medium">{previewDocument.notes}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Policy Detail Dialog */}
      <Dialog open={!!selectedPolicy} onOpenChange={(open) => !open && setSelectedPolicy(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Young Serif, serif' }}>
              <NestIcon className="inline h-5 w-5 mr-2 text-primary" />
              Policy Details
            </DialogTitle>
          </DialogHeader>
          {loadingPolicyDetails ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : selectedPolicy && (
            <div className="pt-4 space-y-6">
              {/* Policy Info */}
              <div className="bg-muted/50 p-4 rounded-lg">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <p className="font-mono text-sm text-muted-foreground">{selectedPolicy.policy_id}</p>
                    <p className="text-xl font-semibold">${selectedPolicy.monthly_rent}/mo rent</p>
                  </div>
                  <Badge className={getStatusBadge(selectedPolicy.status)}>{selectedPolicy.status}</Badge>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Premium</p>
                    <p className="font-medium">${Number(selectedPolicy.monthly_premium).toFixed(2)}/mo</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Coverage Start</p>
                    <p className="font-medium">{selectedPolicy.coverage_start_date ? new Date(selectedPolicy.coverage_start_date).toLocaleDateString() : 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Coverage End</p>
                    <p className="font-medium">{selectedPolicy.coverage_end_date ? new Date(selectedPolicy.coverage_end_date).toLocaleDateString() : 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Next Billing</p>
                    <p className="font-medium">{selectedPolicy.next_billing_date ? new Date(selectedPolicy.next_billing_date).toLocaleDateString() : 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Paid Months</p>
                    <p className="font-medium">{selectedPolicy.paid_months || 0}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Auto-Pay</p>
                    <p className="font-medium">{selectedPolicy.subscription_active ? '✓ Active' : 'Not set up'}</p>
                  </div>
                </div>
              </div>

              {/* Payment Summary */}
              {policyPayments.length > 0 && (
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg text-center">
                    <p className="text-xl font-bold text-green-600">
                      ${policyPayments.reduce((sum, p) => sum + (p.amount || 0), 0).toFixed(2)}
                    </p>
                    <p className="text-xs text-muted-foreground">Total Paid</p>
                  </div>
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg text-center">
                    <p className="text-xl font-bold text-blue-600">
                      {policyPayments.reduce((sum, p) => sum + (p.months_covered || 1), 0)}
                    </p>
                    <p className="text-xs text-muted-foreground">Months</p>
                  </div>
                  <div className="bg-purple-50 dark:bg-purple-900/20 p-3 rounded-lg text-center">
                    <p className="text-xl font-bold text-purple-600">
                      {policyPayments.length}
                    </p>
                    <p className="text-xs text-muted-foreground">Payments</p>
                  </div>
                </div>
              )}

              {/* Payment History */}
              <div>
                <h4 className="font-medium mb-3">Payment History</h4>
                {policyPayments.length > 0 ? (
                  <div className="space-y-2 max-h-60 overflow-auto">
                    {policyPayments.map((payment, idx) => (
                      <div key={payment.payment_id || idx} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${payment.status === 'completed' || payment.status === 'succeeded' ? 'bg-green-100' : 'bg-yellow-100'}`}>
                            {payment.status === 'completed' || payment.status === 'succeeded' ? (
                              <CheckCircle2 className="h-4 w-4 text-green-600" />
                            ) : (
                              <XCircle className="h-4 w-4 text-yellow-600" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-sm">${(payment.amount || 0).toFixed(2)}</p>
                            <p className="text-xs text-muted-foreground">
                              {payment.payment_date ? new Date(payment.payment_date).toLocaleDateString() : 'N/A'}
                              {' • '}{payment.months_covered || 1} month{(payment.months_covered || 1) > 1 ? 's' : ''}
                            </p>
                          </div>
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {payment.payment_type || 'premium'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-muted-foreground">
                    <CreditCard className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No payments recorded for this policy</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AgentLayout>
  );
}
